/**
 * First-party cookie tracking — data layer (feature #164).
 *
 * Thin typed wrappers over `apiClient` for the cookie-profile surface at
 * /api/v1/first-party-cookie. No React / TanStack imports so the module
 * can be unit-tested independently of the page.
 *
 * Endpoints:
 *   POST   /first-party-cookie/profiles
 *   GET    /first-party-cookie/profiles
 *   GET    /first-party-cookie/profiles/:id
 *   PUT    /first-party-cookie/profiles/:id
 *   DELETE /first-party-cookie/profiles/:id
 *   POST   /first-party-cookie/profiles/:id/visit
 *   GET    /first-party-cookie/profiles/:id/metrics
 *
 * A "profile" describes how the edge server writes a first-party cookie
 * on the advertiser's apex domain — Safari ITP will only grant >7-day
 * persistence when the cookie is set as part of a top-level HTML
 * navigation (hence the `itp_safe_mode` flag). `rotation_days` controls
 * how often the edge re-issues the cookie value so the 7-day clock is
 * reset before it fires.
 */

import { apiClient, ApiError } from "@/api-client";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export const SAME_SITE_VALUES = ["Lax", "Strict", "None"] as const;
export type SameSite = (typeof SAME_SITE_VALUES)[number];

export interface ProfileView {
	id: string;
	name: string;
	cookie_name: string;
	domain: string;
	path: string;
	max_age_days: number;
	same_site: SameSite;
	secure: boolean;
	http_only: boolean;
	rotation_days: number;
	itp_safe_mode: boolean;
	enabled: boolean;
	created_at: number;
	updated_at: number;
}

export interface ProfileListResponse {
	count: number;
	profiles: ProfileView[];
}

export interface Metrics {
	visitors: number;
	rotations: number;
	persisted_7d: number;
	max_age_seen_days: number;
}

export interface VisitResponse {
	profile_id: string;
	cookie_name: string;
	cookie_value: string;
	set_cookie: string | null;
	rotated: boolean;
	issued: boolean;
	persisted_7d: boolean;
	age_days: number;
}

export interface CreateProfileInput {
	name: string;
	cookie_name: string;
	domain: string;
	path?: string;
	max_age_days?: number;
	same_site?: SameSite;
	secure?: boolean;
	http_only?: boolean;
	rotation_days?: number;
	itp_safe_mode?: boolean;
	enabled?: boolean;
}

export interface UpdateProfileInput {
	name?: string;
	cookie_name?: string;
	domain?: string;
	path?: string;
	max_age_days?: number;
	same_site?: SameSite;
	secure?: boolean;
	http_only?: boolean;
	rotation_days?: number;
	itp_safe_mode?: boolean;
	enabled?: boolean;
}

// ---------------------------------------------------------------------------
// Pure form helpers — exported so tests cover them without DOM.
// ---------------------------------------------------------------------------

export interface CreateProfileFormInput {
	name?: unknown;
	cookie_name?: unknown;
	domain?: unknown;
	max_age_days?: unknown;
	rotation_days?: unknown;
	same_site?: unknown;
	secure?: unknown;
	http_only?: unknown;
	itp_safe_mode?: unknown;
	enabled?: unknown;
}

export interface CreateProfileParseOk {
	success: true;
	data: CreateProfileInput;
}
export interface CreateProfileParseErr {
	success: false;
	errors: Record<string, string>;
}
export type CreateProfileParseResult =
	| CreateProfileParseOk
	| CreateProfileParseErr;

// Mirrors the server-side rules so the form rejects bad input client-side too.
const COOKIE_NAME_RE = /^[A-Za-z0-9_-]{1,40}$/;
const DOMAIN_RE = /^(?=.{1,253}$)(?!-)([A-Za-z0-9-]{1,63}\.)+[A-Za-z]{2,63}$/;

export const createProfileFormSchema = {
	safeParse(input: CreateProfileFormInput): CreateProfileParseResult {
		const errors: Record<string, string> = {};

		const name =
			typeof input.name === "string" ? input.name.trim() : "";
		if (!name) errors.name = "Profile name is required.";
		else if (name.length > 80) errors.name = "Name must be 80 characters or fewer.";

		const cookie_name =
			typeof input.cookie_name === "string" ? input.cookie_name : "";
		if (!cookie_name) {
			errors.cookie_name = "Cookie name is required.";
		} else if (!COOKIE_NAME_RE.test(cookie_name)) {
			errors.cookie_name =
				"Cookie name must be 1-40 letters, digits, underscore or hyphen.";
		}

		const domainRaw =
			typeof input.domain === "string" ? input.domain.trim().toLowerCase() : "";
		if (!domainRaw) {
			errors.domain = "Cookie domain is required (e.g. shop.example.com).";
		} else if (!DOMAIN_RE.test(domainRaw)) {
			errors.domain = "Enter a valid fully-qualified domain.";
		}

		let max_age_days = 365;
		if (
			input.max_age_days !== undefined &&
			input.max_age_days !== null &&
			input.max_age_days !== ""
		) {
			const n = Number(input.max_age_days);
			if (!Number.isInteger(n) || n < 1 || n > 3650) {
				errors.max_age_days = "Max age must be between 1 and 3650 days.";
			} else {
				max_age_days = n;
			}
		}

		let rotation_days = 6;
		if (
			input.rotation_days !== undefined &&
			input.rotation_days !== null &&
			input.rotation_days !== ""
		) {
			const n = Number(input.rotation_days);
			if (!Number.isInteger(n) || n < 1 || n > 30) {
				errors.rotation_days =
					"Rotation must be between 1 and 30 days.";
			} else {
				rotation_days = n;
			}
		}

		if (
			!errors.max_age_days &&
			!errors.rotation_days &&
			rotation_days >= max_age_days
		) {
			errors.rotation_days = "Rotation must be less than max age.";
		}

		const same_site =
			typeof input.same_site === "string" &&
			(SAME_SITE_VALUES as readonly string[]).includes(input.same_site)
				? (input.same_site as SameSite)
				: "Lax";

		const secure =
			input.secure === undefined ? true : Boolean(input.secure);
		const http_only =
			input.http_only === undefined ? false : Boolean(input.http_only);
		const itp_safe_mode =
			input.itp_safe_mode === undefined ? true : Boolean(input.itp_safe_mode);
		const enabled =
			input.enabled === undefined ? true : Boolean(input.enabled);

		if (same_site === "None" && !secure) {
			errors.secure = "SameSite=None requires Secure.";
		}

		if (Object.keys(errors).length > 0) {
			return { success: false, errors };
		}
		return {
			success: true,
			data: {
				name,
				cookie_name,
				domain: domainRaw,
				max_age_days,
				rotation_days,
				same_site,
				secure,
				http_only,
				itp_safe_mode,
				enabled,
			},
		};
	},
};

/**
 * Format the Set-Cookie header preview shown in the UI. The server is the
 * ultimate source of truth — this is a client-side preview only.
 */
export function previewSetCookieHeader(profile: ProfileView): string {
	const parts: string[] = [];
	parts.push(`${profile.cookie_name}=<cookie_value>`);
	parts.push(`Domain=${profile.domain}`);
	parts.push(`Path=${profile.path}`);
	parts.push(`Max-Age=${profile.max_age_days * 86400}`);
	parts.push(`SameSite=${profile.same_site}`);
	if (profile.secure) parts.push("Secure");
	if (profile.http_only) parts.push("HttpOnly");
	return parts.join("; ");
}

/**
 * Compute the persisted percentage (0..100 integer) from metrics. Used by the
 * page header to surface a quick "is ITP defeating us?" score.
 */
export function persistedPercent(m: Metrics): number {
	if (m.visitors === 0) return 0;
	return Math.round((m.persisted_7d / m.visitors) * 100);
}

/**
 * Human-friendly rotation schedule line.
 */
export function rotationSummary(profile: ProfileView): string {
	return `Rotates every ${profile.rotation_days} day${
		profile.rotation_days === 1 ? "" : "s"
	}, TTL ${profile.max_age_days} day${profile.max_age_days === 1 ? "" : "s"}.`;
}

// ---------------------------------------------------------------------------
// HTTP helpers
// ---------------------------------------------------------------------------

export async function listFirstPartyCookieProfiles(): Promise<ProfileView[]> {
	try {
		const res = await apiClient.get<ProfileListResponse>(
			"/first-party-cookie/profiles",
		);
		return (res?.profiles ?? []) as ProfileView[];
	} catch (err) {
		if (err instanceof ApiError && err.status >= 400 && err.status < 500) {
			return [];
		}
		throw err;
	}
}

export async function createFirstPartyCookieProfile(
	input: CreateProfileInput,
): Promise<ProfileView> {
	const res = await apiClient.post<{ profile: ProfileView }>(
		"/first-party-cookie/profiles",
		{
			name: input.name,
			cookie_name: input.cookie_name,
			domain: input.domain,
			path: input.path,
			max_age_days: input.max_age_days,
			rotation_days: input.rotation_days,
			same_site: input.same_site,
			secure: input.secure,
			http_only: input.http_only,
			itp_safe_mode: input.itp_safe_mode,
			enabled: input.enabled,
		},
	);
	return res.profile;
}

export async function updateFirstPartyCookieProfile(
	id: string,
	patch: UpdateProfileInput,
): Promise<ProfileView> {
	const res = await apiClient.put<{ profile: ProfileView }>(
		`/first-party-cookie/profiles/${encodeURIComponent(id)}`,
		patch,
	);
	return res.profile;
}

export async function deleteFirstPartyCookieProfile(
	id: string,
): Promise<{ id: string; deleted: boolean }> {
	return apiClient.delete<{ id: string; deleted: boolean }>(
		`/first-party-cookie/profiles/${encodeURIComponent(id)}`,
	);
}

export async function fetchFirstPartyCookieMetrics(
	id: string,
): Promise<Metrics> {
	const res = await apiClient.get<{ profile_id: string; metrics: Metrics }>(
		`/first-party-cookie/profiles/${encodeURIComponent(id)}/metrics`,
	);
	return res.metrics;
}

export async function simulateFirstPartyCookieVisit(
	id: string,
	cookieValue?: string,
): Promise<VisitResponse> {
	return apiClient.post<VisitResponse>(
		`/first-party-cookie/profiles/${encodeURIComponent(id)}/visit`,
		cookieValue ? { cookie_value: cookieValue } : {},
	);
}

// ---------------------------------------------------------------------------
// Paths (stable strings the page uses for doc hints + deep links).
// ---------------------------------------------------------------------------

export const firstPartyCookiePaths = {
	base: "/first-party-cookie/profiles",
	visit: (id: string) =>
		`/first-party-cookie/profiles/${encodeURIComponent(id)}/visit`,
	metrics: (id: string) =>
		`/first-party-cookie/profiles/${encodeURIComponent(id)}/metrics`,
} as const;
